import { useEffect, useState } from "react";
import UseTranslationHook from "../../../hooks/UseTranslationHook";
import {
  IServiceCategory,
  fetchServiceCategoryApiCall,
  getServiceCategoryFields,
  handleActiveInactiveServiceCategory,
  handleDeleteServiceCategory,
} from "./ProductServiceCategoryController";
import usePagination from "../../../hooks/UsePaginationHook";
import { PER_PAGE_ROWS } from "../../../config/AppConfig";
import {
  editIcon,
  eyeIcon,
  restoreIcon,
} from "../../../assets/icons/SvgIconList";
import AdvanceSearch from "../../../components/page-layouts/AdvanceSearch";
import ICTable, { ITabelFieldViewProps } from "../../../core-component/ICTable";
import ServiceCategoryForm from "./ProductServiceCategoryForm";
import { IPagination } from "../../../data/AppInterface";
import { ORDER_BY } from "../../../utils/AppConstants";
import ICSweetAlertModal from "../../../core-component/ICSweetAlertModal";
import { truncateText } from "../../../utils/AppFunctions";
import ICImageView from "../../../core-component/ic-image/ICImageView";

const ProductServiceCategory = () => {
  const { t } = UseTranslationHook();
  let timer: NodeJS.Timeout;

  const [isLoading, setIsLoading] = useState(true);
  const [serviceCategoryList, setServiceCategoryList] = useState<
    IServiceCategory[]
  >([]);
  const [serviceCategoryToEdit, setServiceCategoryToEdit] =
    useState<IServiceCategory>();
  const [showForm, setShowForm] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [readOnlyForm, setReadOnlyForm] = useState(false);
  const [deleteAll, setDeleteAll] = useState(false);

  const { pagination, setPagination } = usePagination({
    pagination: {
      perPageRows: Number(PER_PAGE_ROWS),
      currentPage: 1,
      sortBy: "category_title",
      orderBy: "ASC",
    },
  });

  useEffect(() => {
    fetchServiceCategory(pagination);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [enterpriseSearch, deleteAll]);

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnlyForm(false);
    if (serviceCategoryToEdit) {
      setServiceCategoryToEdit(undefined);
    } else {
      setEnterpriseSearch("");
    }
    if (status) {
      fetchServiceCategory(pagination);
    }
  };

  const fetchServiceCategory = (payloadPagination: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await fetchServiceCategoryApiCall(
        setServiceCategoryList,
        setPagination,
        payloadPagination,
        { enterpriseSearch, deleteAll }
      );
      setIsLoading(false);
    }, 500);
  };

  const handleServiceCategoryStatus = async (values: IServiceCategory) => {
    var isStatusChange = await handleActiveInactiveServiceCategory(
      values.id,
      values.is_active === "1" ? "0" : "1"
    );
    if (isStatusChange) {
      const updatedServiceCategoryList = serviceCategoryList.map(
        (serviceCategory) => {
          if (serviceCategory.id === values.id) {
            return {
              ...serviceCategory,
              is_active: values.is_active === "1" ? "0" : "1",
            };
          }
          return serviceCategory;
        }
      );

      setServiceCategoryList(updatedServiceCategoryList);
    }
  };
  const handleDeleteCfm = async (id: number) => {
    const data = await handleDeleteServiceCategory(id, t);
    if (data) {
      fetchServiceCategory(pagination);
    }
  };
  const handleServiceCategoryFormat = (
    item: ITabelFieldViewProps<IServiceCategory>
  ) => {
    return (
      <div className="flex items-center">
        <div className="flex lg:justify-center justify-end">
          {item.data.poster_image ? (
            <ICImageView
              url={`${item.data.poster_image}`}
              className="h-6 w-6 rounded-full"
            />
          ) : (
            <img
              className="w-6 h-6 rounded-full"
              src={require("../../../assets/image/noImage.png")}
              alt="posterImage"
            />
          )}
        </div>
        <div className="ml-1">{item.data.category_title}</div>
      </div>
    );
  };

  const customActionView = (item: IServiceCategory) => {
    return (
      <div>
        {item.deleted === "1" ? (
          <div
            className="flex item-center justify-center
           ml-2"
          >
            <div
              className="mr-2 action-view"
              title={t("restore")}
              onClick={() => handleDeleteCfm(item.id)}
            >
              {restoreIcon}
            </div>
          </div>
        ) : (
          <div className="flex item-center justify-center ml-2">
            <div
              className="mr-2 action-view"
              title={t("view_tooltip_icon")}
              onClick={() => {
                setShowForm(true);
                setServiceCategoryToEdit(item);
                setReadOnlyForm(true);
              }}
            >
              {eyeIcon}
            </div>
            <div
              className="mr-2 action-view "
              title={t("edit_tooltip_icon")}
              onClick={() => {
                setShowForm(true);
                setServiceCategoryToEdit(item);
              }}
            >
              {editIcon}
            </div>
            <div className="action-view " title={t("delete_tooltip_icon")}>
              <ICSweetAlertModal onConfirm={handleDeleteCfm} itemId={item.id} />
            </div>
          </div>
        )}
      </div>
    );
  };
  const allServiceCategoryList = serviceCategoryList.map((val) => ({
    ...val,
    IsDeleted: val.deleted === "1" ? true : false,
  }));
  return (
    <div className="w-full overflow-auto scrollbar-remove">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        handleDeleteCheckBox={(ans) => {
          setDeleteAll(ans);
        }}
        filterCheckBox={deleteAll}
        refreshTable={() => fetchServiceCategory(pagination)}
      />
      <ICTable<IServiceCategory>
        isLoading={isLoading}
        totalFlex="grid grid-cols-17"
        fields={getServiceCategoryFields(
          handleServiceCategoryFormat,
          handleServiceCategoryStatus,
          customActionView,
          t
        )}
        data={allServiceCategoryList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchServiceCategory({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchServiceCategory({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchServiceCategory({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showForm && (
        <ServiceCategoryForm
          handleClose={handleCloseForm}
          serviceCategoryToEdit={serviceCategoryToEdit}
          readOnly={readOnlyForm}
          showForm={showForm}
        />
      )}
    </div>
  );
};

export default ProductServiceCategory;
